import { useEffect } from 'react';
import ReactGA from 'react-ga4';

export const useSEO = ({ 
    title, 
    description, 
    image, 
    keywords, 
    url, 
    type = 'website',
    author = 'AM CARDMATE',
    locale = 'en_GB',
    twitterHandle = '@amcardmate'
}) => {
    useEffect(() => {
        document.title = title;

        const getAbsoluteUrl = (path) => {
            if (!path) return null;
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }
            const cleanPath = path.startsWith('/') ? path : `/${path}`;
            return `https://amcardmate.com${cleanPath}`;
        };

        const updateMeta = (name, content) => {
            if (!content) return;
            let meta = document.querySelector(`meta[name="${name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.name = name;
                document.head.appendChild(meta);
            }
            meta.content = content;
        };

        const updateProperty = (property, content) => {
            if (!content) return;
            let meta = document.querySelector(`meta[property="${property}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('property', property);
                document.head.appendChild(meta);
            }
            meta.content = content;
        };

        updateMeta('description', description);
        if (keywords) updateMeta('keywords', keywords);
        updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
        updateMeta('googlebot', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
        updateMeta('author', author);
        updateMeta('generator', 'React 18');
        updateMeta('format-detection', 'telephone=no');
        updateMeta('theme-color', '#4DA2FF');
        updateMeta('referrer', 'origin-when-cross-origin');
        updateMeta('apple-mobile-web-app-capable', 'yes');
        updateMeta('apple-mobile-web-app-status-bar-style', 'default');
        updateMeta('apple-mobile-web-app-title', 'AM CARDMATE');

        if (!document.querySelector('meta[name="viewport"]')) {
            const viewport = document.createElement('meta');
            viewport.name = 'viewport';
            viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';
            document.head.appendChild(viewport);
        }

        updateProperty('og:title', title);
        updateProperty('og:description', description);
        updateProperty('og:type', type);
        updateProperty('og:site_name', 'AM CARDMATE');
        updateProperty('og:locale', locale);

        const pageUrl = url ? getAbsoluteUrl(url) : window.location.href;
        updateProperty('og:url', pageUrl);

        if (image) {
            const imageUrl = getAbsoluteUrl(image);
            updateProperty('og:image', imageUrl);
            updateProperty('og:image:secure_url', imageUrl);
            updateProperty('og:image:width', '1200');
            updateProperty('og:image:height', '630');
            updateProperty('og:image:alt', title);
            updateProperty('og:image:type', 'image/jpeg');
        }

        updateMeta('twitter:card', 'summary_large_image');
        updateMeta('twitter:site', twitterHandle);
        updateMeta('twitter:creator', twitterHandle);
        updateMeta('twitter:title', title);
        updateMeta('twitter:description', description);
        updateMeta('twitter:domain', 'amcardmate.com');
        if (image) {
            const imageUrl = getAbsoluteUrl(image);
            updateMeta('twitter:image', imageUrl);
            updateMeta('twitter:image:alt', title);
        }

        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.rel = 'canonical';
            document.head.appendChild(link);
        }
        link.href = pageUrl;

        if (!document.querySelector('link[rel="alternate"]')) {
            const alternateLink = document.createElement('link');
            alternateLink.rel = 'alternate';
            alternateLink.hreflang = 'x-default';
            alternateLink.href = pageUrl;
            document.head.appendChild(alternateLink);
        }

        const structuredData = {
            "@context": "https://schema.org",
            "@type": type === 'article' ? 'Article' : 'WebSite',
            "name": "AM CARDMATE",
            "headline": title,
            "description": description,
            "url": pageUrl,
            "image": image ? getAbsoluteUrl(image) : undefined,
            "publisher": {
                "@type": "Organization",
                "name": "AM CARDMATE",
                "url": "https://amcardmate.com",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://amcardmate.com/logo192.png",
                    "width": 192,
                    "height": 192
                },
                "sameAs": [
                    "https://twitter.com/amcardmate",
                    "https://www.linkedin.com/company/amcardmate",
                    "https://www.facebook.com/amcardmate",
                    "https://www.instagram.com/amcardmate"
                ]
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "author": {
                "@type": "Organization",
                "name": "AM CARDMATE"
            },
            "inLanguage": "en-GB",
            "potentialAction": {
                "@type": "SearchAction",
                "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://amcardmate.com/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
            }
        };

        if (type === 'website') {
            structuredData["@type"] = ["WebSite", "LocalBusiness", "SoftwareApplication"];
            structuredData["applicationCategory"] = "BusinessApplication";
            structuredData["operatingSystem"] = "iOS, Android, Web";
            structuredData["offers"] = {
                "@type": "AggregateOffer",
                "priceCurrency": "GBP",
                "lowPrice": "0",
                "highPrice": "99.99"
            };
            structuredData["aggregateRating"] = {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "reviewCount": "500"
            };
        }

        let scriptTag = document.querySelector('script[type="application/ld+json"][data-react-helmet="true"]');
        if (!scriptTag) {
            scriptTag = document.createElement('script');
            scriptTag.type = 'application/ld+json';
            scriptTag.setAttribute('data-react-helmet', 'true');
            document.head.appendChild(scriptTag);
        }
        scriptTag.textContent = JSON.stringify(structuredData);

        const breadcrumbData = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://amcardmate.com"
                }
            ]
        };

        if (url && url !== '/') {
            const pathParts = url.split('/').filter(Boolean);
            pathParts.forEach((part, index) => {
                const name = part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
                breadcrumbData.itemListElement.push({
                    "@type": "ListItem",
                    "position": index + 2,
                    "name": name,
                    "item": `https://amcardmate.com/${pathParts.slice(0, index + 1).join('/')}`
                });
            });
        }

        let breadcrumbScript = document.querySelector('script[type="application/ld+json"][data-breadcrumb="true"]');
        if (!breadcrumbScript) {
            breadcrumbScript = document.createElement('script');
            breadcrumbScript.type = 'application/ld+json';
            breadcrumbScript.setAttribute('data-breadcrumb', 'true');
            document.head.appendChild(breadcrumbScript);
        }
        breadcrumbScript.textContent = JSON.stringify(breadcrumbData);

        const faqData = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is AM CARDMATE?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "AM CARDMATE is a comprehensive digital business card platform that allows professionals to create, share, and manage digital business cards with NFC technology, QR codes, and real-time analytics."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How much does AM CARDMATE cost?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "AM CARDMATE offers flexible pricing: Monthly plan at £4.99, Annual plan at £39.99 (save 33%), and a Lifetime plan at £99.99 which includes a free premium NFC card."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Is AM CARDMATE available on mobile?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes, AM CARDMATE is available as native apps for both iOS and Android devices, as well as a web platform accessible from any browser."
                    }
                }
            ]
        };

        if (window.location.pathname === '/' || window.location.pathname.includes('faq')) {
            let faqScript = document.querySelector('script[type="application/ld+json"][data-faq="true"]');
            if (!faqScript) {
                faqScript = document.createElement('script');
                faqScript.type = 'application/ld+json';
                faqScript.setAttribute('data-faq', 'true');
                document.head.appendChild(faqScript);
            }
            faqScript.textContent = JSON.stringify(faqData);
        }

        if (!document.querySelector('link[rel="preconnect"][href="https://fonts.googleapis.com"]')) {
            const preconnect1 = document.createElement('link');
            preconnect1.rel = 'preconnect';
            preconnect1.href = 'https://fonts.googleapis.com';
            document.head.appendChild(preconnect1);

            const preconnect2 = document.createElement('link');
            preconnect2.rel = 'preconnect';
            preconnect2.href = 'https://fonts.gstatic.com';
            preconnect2.crossOrigin = 'anonymous';
            document.head.appendChild(preconnect2);
        }

        if (ReactGA.isInitialized) {
            ReactGA.send({ hitType: "pageview", page: window.location.pathname });
        }

    }, [title, description, image, keywords, url, type, author, locale, twitterHandle]);
};